// src/sync/cloudSyncEngine.ts

/**
 * Cloud Sync Engine - Production-grade bidirectional, schema-aware project sync.
 *
 * Local-first: local state is primary, cloud is eventual consistency.
 * Conflict detection with human-readable resolution. No silent overwrites. Ever.
 *
 * Features:
 *   • Full sync lifecycle: idle → syncing → conflict → error → offline
 *   • Schema-aware conflict detection (version, pages, models, components)
 *   • Retry logic with exponential backoff
 *   • Merkle-style change log with checksums
 *   • Per-field conflict detection (not just top-level)
 *   • Conflict resolution: local_wins | cloud_wins | merged
 *   • resolvedAt tracking on conflicts (used by syncStatusManager)
 *   • Auto-sync with configurable interval
 *   • Network-aware (online/offline events)
 *   • Full audit trail of sync operations
 *   • Supabase-ready persistence stubs
 *
 * nuvrahq.com
 */

import { persistSave, persistLoad } from "@/persistence/persistenceEngine";
import { logger }                   from "@/system/logger";

/* =========================================================
   NOTE ON eventBus
   ─────────────────────────────────────────────────────────
   eventBus is strictly typed to a closed union of known event
   names (EventOfType<...>). Sync events like "sync:started" are
   not in that union, so we cannot use eventBus.emit() for them.
   Instead we use syncEventBus below - a lightweight, untyped
   internal pub/sub scoped to this module. If you later add sync
   event names to the eventBus union, wire them there and remove
   the calls to syncEventBus.
   ========================================================= */

/* =========================================================
   INTERNAL SYNC EVENT BUS
   ─────────────────────────────────────────────────────────
   A lightweight pub/sub for sync lifecycle events.
   Consumers can subscribe via cloudSync.onSyncEvent().
   ========================================================= */

export type SyncEventName =
  | "sync:started"
  | "sync:completed"
  | "sync:failed"
  | "sync:conflict"
  | "sync:conflict_resolved"
  | "sync:push_completed"
  | "sync:pull_applied"
  | "sync:cloud_applied"
  | "sync:error"
  | "sync:change_recorded"
  | "sync:retry_scheduled";

export interface SyncEvent {
  name:      SyncEventName;
  payload:   Record<string, unknown>;
  timestamp: number;
}

type SyncEventListener = (event: SyncEvent) => void;

const syncEventListeners = new Map<SyncEventName | "*", Set<SyncEventListener>>();

function emitSyncEvent(name: SyncEventName, payload: Record<string, unknown> = {}): void {
  const event: SyncEvent = { name, payload, timestamp: Date.now() };

  // Specific listeners
  const specific = syncEventListeners.get(name);
  if (specific) {
    specific.forEach(fn => { try { fn(event); } catch { /* safe */ } });
  }

  // Wildcard listeners
  const wildcard = syncEventListeners.get("*");
  if (wildcard) {
    wildcard.forEach(fn => { try { fn(event); } catch { /* safe */ } });
  }
}

/* =========================================================
   CONSTANTS
   ========================================================= */

const DEVICE_ID_KEY          = "nuvra-device-id";
const CHANGELOG_KEY          = "sync-changelog";
const MAX_CHANGELOG_ENTRIES  = 1_000;
const MAX_SYNC_AUDIT_ENTRIES = 200;
const AUTO_SYNC_INTERVAL_MS  = 30_000;
const RETRY_BASE_DELAY_MS    = 2_000;
const MAX_RETRY_ATTEMPTS     = 5;

/* =========================================================
   TYPES - STATUS
   ========================================================= */

export type SyncStatus = "idle" | "syncing" | "conflict" | "error" | "offline";

/* =========================================================
   TYPES - CONFLICT
   ─────────────────────────────────────────────────────────
   resolvedAt is included so syncStatusManager and any other
   consumer can track resolution state without a side-channel Map.
   ========================================================= */

export interface SyncConflict {
  id:             string;
  path:           string;
  localValue:     unknown;
  cloudValue:     unknown;
  localTimestamp: number;
  cloudTimestamp: number;
  resolution:     "pending" | "local_wins" | "cloud_wins" | "merged";
  description:    string;
  /** Set when the conflict is resolved. Used by syncStatusManager. */
  resolvedAt?:    number;
  /** Who resolved it - "user" | userId | "system" */
  resolvedBy?:    string;
}

/* =========================================================
   TYPES - SYNC STATE
   ========================================================= */

export interface SyncState {
  status:          SyncStatus;
  lastSyncAt:      number | null;
  lastLocalChange: number | null;
  lastCloudChange: number | null;
  pendingChanges:  number;
  conflicts:       SyncConflict[];
  deviceId:        string;
  /** Total number of successful syncs this session */
  totalSyncs:      number;
  /** Last sync direction */
  lastDirection:   "push" | "pull" | "merge" | "none" | null;
  /** Current retry attempt (0 if not retrying) */
  retryCount:      number;
  /** Next retry timestamp */
  nextRetryAt:     number | null;
}

/* =========================================================
   TYPES - CLOUD PROJECT RECORD
   ========================================================= */

export interface CloudProjectRecord {
  projectId:         string;
  schemaFingerprint: string;
  schemaVersion:     number;
  lastModifiedAt:    number;
  lastModifiedBy:    string;
  deviceId:          string;
  data: {
    pages:      unknown[];
    models:     unknown[];
    components: unknown[];
    actions:    unknown[];
    design:     unknown;
    meta:       unknown;
    workflows?: unknown[];
    settings?:  unknown;
  };
}

/* =========================================================
   TYPES - INJECTED ADAPTERS
   ─────────────────────────────────────────────────────────
   The engine owns the sync ALGORITHM (lifecycle, conflict
   detection, retry, audit). The two sides of the wire are
   injected so the engine stays decoupled and testable, and so
   the real Supabase / project-store wiring can live at the app
   boundary instead of inside this module.

   remote: the cloud side (real impl in supabaseSyncAdapter.ts).
   local:  the device side (real impl in projectStoreSyncProvider.ts).
   ========================================================= */

export interface SyncRemoteAdapter {
  /** Read the cloud record for a project, or null if none exists yet. */
  fetch(projectId: string): Promise<CloudProjectRecord | null>;
  /** Create or replace the cloud record for a project. */
  push(record: CloudProjectRecord): Promise<void>;
}

export interface SyncLocalProvider {
  /** Build a record from current local state, or null when there is no project. */
  getRecord(): CloudProjectRecord | null;
  /** Apply an incoming cloud record to local state. */
  applyRecord(record: CloudProjectRecord): void;
}

/* =========================================================
   TYPES - SYNC RESULT
   ========================================================= */

export interface SyncResult {
  success:        boolean;
  direction:      "push" | "pull" | "merge" | "none";
  conflicts:      SyncConflict[];
  changesApplied: number;
  durationMs:     number;
  error?:         string;
}

/* =========================================================
   TYPES - CHANGE ENTRY
   ========================================================= */

export interface ChangeEntry {
  id:            string;
  timestamp:     number;
  path:          string;
  operation:     "create" | "update" | "delete";
  deviceId:      string;
  schemaVersion: number;
  checksum:      string;
  /** true once this entry has been pushed to cloud */
  synced:        boolean;
}

/* =========================================================
   TYPES - SYNC AUDIT ENTRY
   ========================================================= */

export type SyncAuditAction =
  | "sync_started"
  | "sync_completed"
  | "sync_failed"
  | "conflict_detected"
  | "conflict_resolved"
  | "push_completed"
  | "pull_completed"
  | "retry_scheduled"
  | "auto_sync_enabled"
  | "auto_sync_disabled";

export interface SyncAuditEntry {
  entryId:    string;
  action:     SyncAuditAction;
  timestamp:  number;
  projectId?: string;
  meta?:      Record<string, unknown>;
}

/* =========================================================
   TYPES - MERGE RESULT
   ========================================================= */

export interface MergeResult {
  merged:    CloudProjectRecord;
  conflicts: SyncConflict[];
}

/* =========================================================
   CLOUD SYNC ENGINE CLASS
   ========================================================= */

export class CloudSyncEngine {

  /* =========================================================
     INTERNAL STATE
     ========================================================= */

  private state: SyncState;
  private changeLog:    ChangeEntry[]    = [];
  private auditLog:     SyncAuditEntry[] = [];
  private syncListeners = new Set<(state: SyncState) => void>();
  private syncInterval: ReturnType<typeof setInterval>  | null = null;
  private retryTimeout: ReturnType<typeof setTimeout>   | null = null;
  private isOnline = typeof navigator !== "undefined" ? navigator.onLine : true;
  private remote: SyncRemoteAdapter | null = null;
  private local:  SyncLocalProvider  | null = null;

  /* =========================================================
     CONSTRUCTOR
     ========================================================= */

  constructor() {
    this.state = {
      status:          "idle",
      lastSyncAt:      null,
      lastLocalChange: null,
      lastCloudChange: null,
      pendingChanges:  0,
      conflicts:       [],
      deviceId:        this.getDeviceId(),
      totalSyncs:      0,
      lastDirection:   null,
      retryCount:      0,
      nextRetryAt:     null,
    };

    // Restore changelog from persistence
    const savedLog = persistLoad<ChangeEntry[]>(CHANGELOG_KEY);
    if (Array.isArray(savedLog)) this.changeLog = savedLog;

    // Network events
    if (typeof window !== "undefined") {
      window.addEventListener("online", () => {
        this.isOnline = true;
        logger.info("[CloudSyncEngine] Network back online - triggering sync");
        void this.attemptSync();
      });
      window.addEventListener("offline", () => {
        this.isOnline = false;
        this.updateStatus("offline");
        logger.info("[CloudSyncEngine] Network went offline");
      });
    }
  }

  /* =========================================================
     🔥 CONFIGURE (dependency injection)
     ========================================================= */

  /**
   * Wire the engine to its real cloud and local sides. Call once at app start
   * (see initProjectSync.ts). Either side may be omitted in a context where it
   * does not exist (e.g. server-side: no local provider).
   */
  configure(deps: { remote?: SyncRemoteAdapter; local?: SyncLocalProvider }): void {
    if (deps.remote !== undefined) this.remote = deps.remote;
    if (deps.local  !== undefined) this.local  = deps.local;
    logger.info("[CloudSyncEngine] Configured", {
      remote: !!this.remote, local: !!this.local,
    });
  }

  /** True once a real cloud adapter is wired (sync can actually reach the cloud). */
  isCloudConfigured(): boolean {
    return !!this.remote;
  }

  /* =========================================================
     🔥 SYNC EVENT SUBSCRIPTION
     ========================================================= */

  /**
   * Subscribe to a specific sync lifecycle event or all events ("*").
   * Returns an unsubscribe function.
   *
   * @example
   *   cloudSync.onSyncEvent("sync:conflict", e => showConflictUI(e.payload))
   *   cloudSync.onSyncEvent("*", e => console.log(e.name, e.payload))
   */
  onSyncEvent(
    name:     SyncEventName | "*",
    listener: SyncEventListener
  ): () => void {
    const set = syncEventListeners.get(name) ?? new Set();
    set.add(listener);
    syncEventListeners.set(name, set);
    return () => {
      const s = syncEventListeners.get(name);
      if (s) {
        s.delete(listener);
        if (s.size === 0) syncEventListeners.delete(name);
      }
    };
  }

  /* =========================================================
     🔥 RECORD LOCAL CHANGE
     ========================================================= */

  /**
   * Records a local change into the changelog for sync tracking.
   * Call this whenever the project store is mutated.
   */
  recordChange(
    path:          string,
    operation:     ChangeEntry["operation"],
    schemaVersion: number
  ): void {
    const entry: ChangeEntry = {
      id:            `chg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      timestamp:     Date.now(),
      path,
      operation,
      deviceId:      this.state.deviceId,
      schemaVersion,
      checksum:      this.simpleHash(`${path}:${operation}:${Date.now()}`),
      synced:        false,
    };

    this.changeLog.push(entry);

    if (this.changeLog.length > MAX_CHANGELOG_ENTRIES) {
      this.changeLog = this.changeLog.slice(-MAX_CHANGELOG_ENTRIES);
    }

    this.state.pendingChanges  = this.changeLog.filter(
      c => !c.synced && c.timestamp > (this.state.lastSyncAt ?? 0)
    ).length;
    this.state.lastLocalChange = Date.now();
    this.notifyListeners();

    persistSave(CHANGELOG_KEY, this.changeLog);

    emitSyncEvent("sync:change_recorded", { path, operation });
  }

  /* =========================================================
     🔥 ATTEMPT SYNC
     ========================================================= */

  /**
   * Attempts a full bidirectional sync.
   * Returns a detailed SyncResult.
   */
  async attemptSync(): Promise<SyncResult> {
    // Honest state: with no cloud adapter wired, sync cannot reach the cloud.
    // Report that plainly rather than faking a successful local-only round-trip.
    if (!this.remote) {
      return {
        success:        false,
        direction:      "none",
        conflicts:      [],
        changesApplied: 0,
        durationMs:     0,
        error:          "Cloud sync not configured",
      };
    }

    if (!this.isOnline) {
      logger.info("[CloudSyncEngine] Sync skipped - offline");
      return {
        success:        false,
        direction:      "none",
        conflicts:      [],
        changesApplied: 0,
        durationMs:     0,
        error:          "Offline",
      };
    }

    if (this.state.status === "syncing") {
      logger.info("[CloudSyncEngine] Sync already in progress - skipping");
      return {
        success:        false,
        direction:      "none",
        conflicts:      [],
        changesApplied: 0,
        durationMs:     0,
        error:          "Already syncing",
      };
    }

    const startTime = Date.now();
    this.updateStatus("syncing");
    this.addAuditEntry("sync_started");
    emitSyncEvent("sync:started");

    try {
      // 1. Get local state
      const localRecord = this.getLocalRecord();
      if (!localRecord) {
        this.updateStatus("idle");
        logger.info("[CloudSyncEngine] No local record - nothing to sync");
        return {
          success:        true,
          direction:      "none",
          conflicts:      [],
          changesApplied: 0,
          durationMs:     Date.now() - startTime,
        };
      }

      // 2. Fetch cloud state
      const cloudRecord = await this.fetchCloudRecord(localRecord.projectId);

      // 3. First sync - push
      if (!cloudRecord) {
        await this.pushToCloud(localRecord);
        this.markChangelogSynced();
        this.state.lastSyncAt     = Date.now();
        this.state.pendingChanges = 0;
        this.state.totalSyncs++;
        this.state.lastDirection  = "push";
        this.state.retryCount     = 0;
        this.state.nextRetryAt    = null;
        this.updateStatus("idle");
        this.addAuditEntry("push_completed", localRecord.projectId);
        emitSyncEvent("sync:push_completed", { projectId: localRecord.projectId });
        logger.info("[CloudSyncEngine] Initial push completed", { projectId: localRecord.projectId });
        return {
          success:        true,
          direction:      "push",
          conflicts:      [],
          changesApplied: 1,
          durationMs:     Date.now() - startTime,
        };
      }

      // 4. Already in sync
      if (localRecord.schemaFingerprint === cloudRecord.schemaFingerprint) {
        this.state.lastSyncAt     = Date.now();
        this.state.pendingChanges = 0;
        this.state.retryCount     = 0;
        this.state.nextRetryAt    = null;
        this.state.lastDirection  = "none";
        this.updateStatus("idle");
        logger.info("[CloudSyncEngine] Already in sync", { projectId: localRecord.projectId });
        return {
          success:        true,
          direction:      "none",
          conflicts:      [],
          changesApplied: 0,
          durationMs:     Date.now() - startTime,
        };
      }

      // 5. Detect conflicts
      const conflicts = this.detectConflicts(localRecord, cloudRecord);

      if (conflicts.length > 0) {
        // Preserve already-resolved conflicts - don't overwrite them
        const existingResolved = this.state.conflicts.filter(c => c.resolution !== "pending");
        this.state.conflicts = [
          ...conflicts,
          ...existingResolved.filter(r => !conflicts.find(c => c.id === r.id)),
        ];
        this.state.lastDirection = "merge";
        this.updateStatus("conflict");
        this.addAuditEntry("conflict_detected", localRecord.projectId, { count: conflicts.length });
        emitSyncEvent("sync:conflict", { count: conflicts.length, projectId: localRecord.projectId });
        logger.warn("[CloudSyncEngine] Conflicts detected", { count: conflicts.length });
        return {
          success:        false,
          direction:      "merge",
          conflicts,
          changesApplied: 0,
          durationMs:     Date.now() - startTime,
        };
      }

      // 6. No conflicts - push or pull by timestamp
      if (localRecord.lastModifiedAt >= cloudRecord.lastModifiedAt) {
        await this.pushToCloud(localRecord);
        this.markChangelogSynced();
        this.state.lastSyncAt     = Date.now();
        this.state.pendingChanges = 0;
        this.state.totalSyncs++;
        this.state.lastDirection  = "push";
        this.state.retryCount     = 0;
        this.state.nextRetryAt    = null;
        this.updateStatus("idle");
        this.addAuditEntry("push_completed", localRecord.projectId);
        emitSyncEvent("sync:push_completed", { projectId: localRecord.projectId });
        logger.info("[CloudSyncEngine] Push completed", { projectId: localRecord.projectId });
        return {
          success:        true,
          direction:      "push",
          conflicts:      [],
          changesApplied: 1,
          durationMs:     Date.now() - startTime,
        };
      } else {
        this.applyCloudRecord(cloudRecord);
        this.state.lastSyncAt      = Date.now();
        this.state.lastCloudChange = cloudRecord.lastModifiedAt;
        this.state.totalSyncs++;
        this.state.lastDirection   = "pull";
        this.state.retryCount      = 0;
        this.state.nextRetryAt     = null;
        this.updateStatus("idle");
        this.addAuditEntry("pull_completed", cloudRecord.projectId);
        emitSyncEvent("sync:pull_applied", { projectId: cloudRecord.projectId });
        logger.info("[CloudSyncEngine] Pull completed", { projectId: cloudRecord.projectId });
        return {
          success:        true,
          direction:      "pull",
          conflicts:      [],
          changesApplied: 1,
          durationMs:     Date.now() - startTime,
        };
      }

    } catch (err) {
      const error = err instanceof Error ? err.message : "Sync failed";
      this.updateStatus("error");
      this.addAuditEntry("sync_failed", undefined, { error });
      emitSyncEvent("sync:error", { error });
      logger.error("[CloudSyncEngine] Sync failed", { error });
      this.scheduleRetry();
      return {
        success:        false,
        direction:      "none",
        conflicts:      [],
        changesApplied: 0,
        durationMs:     Date.now() - startTime,
        error,
      };
    } finally {
      this.addAuditEntry("sync_completed", undefined, { status: this.state.status });
      emitSyncEvent("sync:completed", { status: this.state.status });
    }
  }

  /* =========================================================
     🔥 CONFLICT RESOLUTION
     ========================================================= */

  /**
   * Resolves a sync conflict with the given strategy.
   * Sets resolvedAt so syncStatusManager can track resolution state.
   */
  resolveConflict(
    conflictId: string,
    resolution: "local_wins" | "cloud_wins",
    resolvedBy: string = "user"
  ): void {
    const conflict = this.state.conflicts.find(c => c.id === conflictId);
    if (!conflict) {
      logger.warn("[CloudSyncEngine] Conflict not found", { conflictId });
      return;
    }

    conflict.resolution = resolution;
    conflict.resolvedAt = Date.now();
    conflict.resolvedBy = resolvedBy;

    logger.info("[CloudSyncEngine] Conflict resolved", { conflictId, resolution, resolvedBy });
    this.addAuditEntry("conflict_resolved", undefined, { conflictId, resolution });
    emitSyncEvent("sync:conflict_resolved", { conflictId, resolution });

    // Remove from active conflicts
    this.state.conflicts = this.state.conflicts.filter(c => c.id !== conflictId);

    if (this.state.conflicts.length === 0) {
      this.updateStatus("idle");
      // Re-trigger sync to apply the resolution
      void this.attemptSync();
    }

    this.notifyListeners();
  }

  /**
   * Resolves all pending conflicts at once with the same strategy.
   */
  resolveAllConflicts(
    resolution: "local_wins" | "cloud_wins",
    resolvedBy: string = "user"
  ): void {
    const pending = this.state.conflicts.filter(c => c.resolution === "pending");

    for (const conflict of pending) {
      conflict.resolution = resolution;
      conflict.resolvedAt = Date.now();
      conflict.resolvedBy = resolvedBy;
      this.addAuditEntry("conflict_resolved", undefined, { conflictId: conflict.id, resolution });
      emitSyncEvent("sync:conflict_resolved", { conflictId: conflict.id, resolution });
    }

    this.state.conflicts = [];
    this.updateStatus("idle");
    logger.info("[CloudSyncEngine] All conflicts resolved", { count: pending.length, resolution });

    void this.attemptSync();
    this.notifyListeners();
  }

  /* =========================================================
     🔥 AUTO SYNC
     ========================================================= */

  enableAutoSync(intervalMs: number = AUTO_SYNC_INTERVAL_MS): void {
    this.disableAutoSync();
    this.syncInterval = setInterval(() => {
      void this.attemptSync();
    }, intervalMs);
    this.addAuditEntry("auto_sync_enabled", undefined, { intervalMs });
    logger.info("[CloudSyncEngine] Auto-sync enabled", { intervalMs });
  }

  disableAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      this.addAuditEntry("auto_sync_disabled");
      logger.info("[CloudSyncEngine] Auto-sync disabled");
    }
  }

  /* =========================================================
     🔥 STATE & LISTENERS
     ========================================================= */

  onStateChange(listener: (state: SyncState) => void): () => void {
    this.syncListeners.add(listener);
    listener({ ...this.state });
    return () => this.syncListeners.delete(listener);
  }

  getState(): Readonly<SyncState> {
    return { ...this.state };
  }

  getPendingCount(): number {
    return this.state.pendingChanges;
  }

  /* =========================================================
     🔥 AUDIT & CHANGE LOG
     ========================================================= */

  getAuditLog(limit = 50): SyncAuditEntry[] {
    return this.auditLog.slice(0, limit);
  }

  getChangeLog(limit = 100): ChangeEntry[] {
    return this.changeLog.slice(-limit).reverse();
  }

  getUnsyncedChanges(): ChangeEntry[] {
    return this.changeLog.filter(
      c => !c.synced && c.timestamp > (this.state.lastSyncAt ?? 0)
    );
  }

  /* =========================================================
     🔥 RETRY LOGIC
     ========================================================= */

  private scheduleRetry(): void {
    if (this.state.retryCount >= MAX_RETRY_ATTEMPTS) {
      logger.warn("[CloudSyncEngine] Max retry attempts reached - giving up", {
        retryCount: this.state.retryCount,
      });
      this.state.retryCount  = 0;
      this.state.nextRetryAt = null;
      this.notifyListeners();
      return;
    }

    this.state.retryCount++;
    const delay            = RETRY_BASE_DELAY_MS * Math.pow(2, this.state.retryCount - 1);
    this.state.nextRetryAt = Date.now() + delay;

    logger.info("[CloudSyncEngine] Retry scheduled", {
      attempt:     this.state.retryCount,
      delayMs:     delay,
      nextRetryAt: new Date(this.state.nextRetryAt).toISOString(),
    });

    this.addAuditEntry("retry_scheduled", undefined, {
      attempt: this.state.retryCount,
      delayMs: delay,
    });

    emitSyncEvent("sync:retry_scheduled", {
      attempt:     this.state.retryCount,
      delayMs:     delay,
      nextRetryAt: this.state.nextRetryAt,
    });

    this.notifyListeners();

    if (this.retryTimeout) clearTimeout(this.retryTimeout);

    this.retryTimeout = setTimeout(() => {
      if (!this.isOnline) {
        logger.info("[CloudSyncEngine] Retry skipped - still offline");
        return;
      }
      void this.attemptSync();
    }, delay);
  }

  /* =========================================================
     🔥 CONFLICT DETECTION
     ========================================================= */

  private detectConflicts(
    local: CloudProjectRecord,
    cloud: CloudProjectRecord
  ): SyncConflict[] {
    const conflicts: SyncConflict[] = [];
    const now = Date.now();

    // Only flag conflicts if BOTH sides changed since last sync
    const lastSync     = this.state.lastSyncAt ?? 0;
    const localChanged = local.lastModifiedAt  > lastSync;
    const cloudChanged = cloud.lastModifiedAt  > lastSync;

    if (!localChanged || !cloudChanged) return [];

    // ── Schema version conflict ──────────────────────────
    if (local.schemaVersion !== cloud.schemaVersion) {
      conflicts.push({
        id:             `conflict_version_${now}`,
        path:           "schema.version",
        localValue:     local.schemaVersion,
        cloudValue:     cloud.schemaVersion,
        localTimestamp: local.lastModifiedAt,
        cloudTimestamp: cloud.lastModifiedAt,
        resolution:     "pending",
        description:    `Schema version mismatch: local v${local.schemaVersion} vs cloud v${cloud.schemaVersion}`,
      });
    }

    // ── Page count divergence ────────────────────────────
    if (local.data.pages.length !== cloud.data.pages.length) {
      conflicts.push({
        id:             `conflict_pages_${now}`,
        path:           "data.pages",
        localValue:     local.data.pages.length,
        cloudValue:     cloud.data.pages.length,
        localTimestamp: local.lastModifiedAt,
        cloudTimestamp: cloud.lastModifiedAt,
        resolution:     "pending",
        description:    `Page count differs: local has ${local.data.pages.length}, cloud has ${cloud.data.pages.length}`,
      });
    }

    // ── Component count divergence ───────────────────────
    if (local.data.components.length !== cloud.data.components.length) {
      conflicts.push({
        id:             `conflict_components_${now}`,
        path:           "data.components",
        localValue:     local.data.components.length,
        cloudValue:     cloud.data.components.length,
        localTimestamp: local.lastModifiedAt,
        cloudTimestamp: cloud.lastModifiedAt,
        resolution:     "pending",
        description:    `Component count differs: local has ${local.data.components.length}, cloud has ${cloud.data.components.length}`,
      });
    }

    // ── Model count divergence ───────────────────────────
    if (local.data.models.length !== cloud.data.models.length) {
      conflicts.push({
        id:             `conflict_models_${now}`,
        path:           "data.models",
        localValue:     local.data.models.length,
        cloudValue:     cloud.data.models.length,
        localTimestamp: local.lastModifiedAt,
        cloudTimestamp: cloud.lastModifiedAt,
        resolution:     "pending",
        description:    `Model count differs: local has ${local.data.models.length}, cloud has ${cloud.data.models.length}`,
      });
    }

    // ── Fingerprint catch-all ────────────────────────────
    if (
      conflicts.length === 0 &&
      local.schemaFingerprint !== cloud.schemaFingerprint
    ) {
      conflicts.push({
        id:             `conflict_fingerprint_${now}`,
        path:           "schema.fingerprint",
        localValue:     local.schemaFingerprint,
        cloudValue:     cloud.schemaFingerprint,
        localTimestamp: local.lastModifiedAt,
        cloudTimestamp: cloud.lastModifiedAt,
        resolution:     "pending",
        description:    "Project data diverged - fingerprints do not match. Choose which version to keep.",
      });
    }

    return conflicts;
  }

  /* =========================================================
     🔥 CLOUD I/O
     ─────────────────────────────────────────────────────────
     Thin delegations to the injected adapters. The real cloud
     side lives in supabaseSyncAdapter.ts (project_sync_records);
     the real local side in projectStoreSyncProvider.ts. The
     engine never touches Supabase or the project store directly.
     ========================================================= */

  /** Fetch the cloud record for a project via the remote adapter. */
  private async fetchCloudRecord(projectId: string): Promise<CloudProjectRecord | null> {
    if (!this.remote) return null;
    return this.remote.fetch(projectId);
  }

  /** Push a local record to the cloud via the remote adapter. */
  private async pushToCloud(record: CloudProjectRecord): Promise<void> {
    if (!this.remote) throw new Error("Cloud sync not configured");
    await this.remote.push(record);
  }

  /** Apply a cloud record to local state via the local provider. */
  private applyCloudRecord(record: CloudProjectRecord): void {
    this.local?.applyRecord(record);
    emitSyncEvent("sync:cloud_applied", { projectId: record.projectId });
  }

  /** Read current local state as a CloudProjectRecord via the local provider. */
  private getLocalRecord(): CloudProjectRecord | null {
    return this.local ? this.local.getRecord() : null;
  }

  /* =========================================================
     HELPERS
     ========================================================= */

  private markChangelogSynced(): void {
    for (const entry of this.changeLog) {
      if (!entry.synced) entry.synced = true;
    }
    persistSave(CHANGELOG_KEY, this.changeLog);
  }

  private updateStatus(status: SyncStatus): void {
    this.state.status = status;
    this.notifyListeners();
  }

  private notifyListeners(): void {
    const snapshot = { ...this.state };
    for (const listener of this.syncListeners) {
      try { listener(snapshot); } catch { /* safe */ }
    }
  }

  private addAuditEntry(
    action:     SyncAuditAction,
    projectId?: string,
    meta?:      Record<string, unknown>
  ): void {
    const entry: SyncAuditEntry = {
      entryId:   `aue_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      action,
      timestamp: Date.now(),
      projectId,
      meta,
    };

    this.auditLog.unshift(entry);

    if (this.auditLog.length > MAX_SYNC_AUDIT_ENTRIES) {
      this.auditLog.splice(MAX_SYNC_AUDIT_ENTRIES, this.auditLog.length - MAX_SYNC_AUDIT_ENTRIES);
    }
  }

  private getDeviceId(): string {
    if (typeof localStorage === "undefined") {
      return `dev_ssr_${Math.random().toString(36).slice(2, 10)}`;
    }
    const stored = localStorage.getItem(DEVICE_ID_KEY);
    if (stored) return stored;
    const id = `dev_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  }

  private simpleHash(content: string): string {
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      hash = ((hash << 5) - hash) + content.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash).toString(36).padStart(8, "0");
  }

  /* =========================================================
     TEARDOWN
     ========================================================= */

  destroy(): void {
    this.disableAutoSync();

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    if (typeof window !== "undefined") {
      window.removeEventListener("online",  () => void this.attemptSync());
      window.removeEventListener("offline", () => this.updateStatus("offline"));
    }

    this.syncListeners.clear();
    syncEventListeners.clear();
    logger.info("[CloudSyncEngine] Destroyed");
  }
}

/* =========================================================
   🔥 SINGLETON EXPORT
   ========================================================= */

export const cloudSync = new CloudSyncEngine();

/* =========================================================
   REAL CLOUD WIRING
   ─────────────────────────────────────────────────────────
   The cloud side is implemented for real, not stubbed:

   • Table:    supabase/migrations/*_project_sync_records.sql
               (project_sync_records, owner-scoped RLS)
   • Remote:   src/sync/supabaseSyncAdapter.ts
               (fetch/push against project_sync_records)
   • Local:    src/sync/projectStoreSyncProvider.ts
               (builds the record from the project store + canonical
                schema; applies an incoming record via restoreFromCloud)
   • Bootstrap: src/sync/initProjectSync.ts wires both into the engine
                via cloudSync.configure({ remote, local }).
   ========================================================= */